import React, { useContext, useEffect, useCallback } from "react";
import * as Sentry from "@sentry/react";
import CustomerList from "src/screens/dashboard/customers/customer/list/CustomerList";
import { observer } from "mobx-react-lite";
import { UI } from "@wwimmo/ui";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import * as Screens from "src/screens";
import { Route } from "src/config/routes";
import styles from "./CustomersOverview.module.css";
import { CustomerOverviewStatistics } from "./CustomerOverviewStatistics";
import { withRouter, RouteComponentProps } from "react-router";
import { Role as CustomerRole } from "src/network/User";
import { CallServiceForAllCustomers } from "./additional-actions/CallServiceForAllCustomers";
import { UpdateGeneralSettingDialog } from "./additional-actions/UpdateGeneralSettingsDialog";
import { NetworkConfig } from "src/network/NetworkConfig";
import { getRoleKey } from "src/network/User";
import { MessageType } from "src/components/notifications/Notifier";
import { getEnvironment } from "src/utils/Customer";
import { CustomerOverviewModal, CustomersOverviewStore } from "src/stores/CustomersOverviewStore";

interface ICustomerDialogProps {
    buttonLabel: string;
    modalTitle: string;
    show: boolean;
    onToggleModal: () => void;
    children: React.ReactNode;
    modalSize?: "sm" | "lg" | "xl";
}

interface ICopyCustomerDialogProps {
    label: string;
}

const CustomerOverviewDialog = (dialogProps: ICustomerDialogProps) => {
    return (
        <>
            <UI.Button
                label={dialogProps.buttonLabel}
                className="customer-button mt-2"
                variant="danger"
                onClick={dialogProps.onToggleModal}
            />

            <UI.Modal
                backdrop={"static"}
                title={dialogProps.modalTitle}
                show={dialogProps.show}
                onClose={dialogProps.onToggleModal}
                size={dialogProps.modalSize}
            >
                {dialogProps.children}
            </UI.Modal>
        </>
    );
};

const CopyCustomerDialog = (props: ICopyCustomerDialogProps) => (
    <UI.Button
        label={props.label}
        className="customer-button mt-2 w-100"
        variant="danger"
        to={Route.customersCopy}
    />
);

const CustomersOverviewBase = (props: RouteComponentProps) => {
    const { t } = useTranslation();
    const { navStore, uiStore, authStore, customersOverviewStore } = useContext(RootStoreContext);

    const listIdentifier = "customerlist";

    if (!navStore.listMap.has(listIdentifier)) {
        navStore.setListMapValue(listIdentifier, {});
    }

    const userRole = authStore.user ? authStore.user.role : undefined;

    const onActualizeZipcodes = useCallback(async () => {
        const actualizeZipCodesEndpointUrl = `${NetworkConfig.actualizeZipcodesUrl}`;

        if (authStore.user && authStore.token) {
            const accessToken = authStore.token;
            const tokenType = authStore.tokenType;
            const role = authStore.user?.role;

            try {
                const fetchResult = await fetch(actualizeZipCodesEndpointUrl, {
                    method: "POST",
                    headers: {
                        Authorization: `${tokenType} ${accessToken}`,
                        "x-hasura-role": getRoleKey(role)
                    }
                });

                if (fetchResult.status === 200) {
                    uiStore.printStatusMessage("Die Postleitzahlen wurden erfolgreich aktualisiert.", MessageType.INFO);
                } else {
                    const response = await fetchResult.json();

                    uiStore.printStatusMessage(`Es ist ein Fehler aufgetreten: ${response.message}`, MessageType.ERROR);
                }
            } catch (fetchError) {
                uiStore.printStatusMessage(`Es ist ein Fehler aufgetreten: ${fetchError}`, MessageType.ERROR);
            }
        } else {
            uiStore.printStatusMessage(
                "Keine Authorisierungs-Informationen vorhanden. Bitte lade die Seite neu.",
                MessageType.ERROR
            );
        }
        customersOverviewStore.setActiveModal(CustomerOverviewModal.NONE);
    }, [authStore.token, authStore.tokenType, authStore.user, customersOverviewStore, uiStore]);

    useEffect(() => {
        navStore.setTitle("Kunden");
        navStore.setBackbutton(null);
    }, [navStore]);

    useEffect(() => {
        customersOverviewStore.initializeFromNavigation();
        customersOverviewStore.loadInitialData();

        return () => {
            customersOverviewStore.reset();
        };
    }, [customersOverviewStore]);

    useEffect(() => {
        if (uiStore.hasNewCustomerData) {
            customersOverviewStore.reloadData();
            uiStore.setHasNewCustomerData(false);
        }
    }, [customersOverviewStore, uiStore]);

    const toggleAcualizeRepxModal = useCallback(() => {
        customersOverviewStore.toggleModal(CustomerOverviewModal.ACTUALIZE_REPX);
    }, [customersOverviewStore]);

    const toggleCleanupFilesModal = useCallback(() => {
        customersOverviewStore.toggleModal(CustomerOverviewModal.CLEANUP_FILES);
    }, [customersOverviewStore]);

    const toggleUpdateApiMinversionModal = useCallback(() => {
        customersOverviewStore.toggleModal(CustomerOverviewModal.UPDATE_API_MINVERSION);
    }, [customersOverviewStore]);

    const toggleUpdateApiCurrentVersionModal = useCallback(() => {
        customersOverviewStore.toggleModal(CustomerOverviewModal.UPDATE_API_CURRENTVERSION);
    }, [customersOverviewStore]);

    if (
        customersOverviewStore.customersLoading &&
        customersOverviewStore.customers.length === 0 &&
        customersOverviewStore.searchQuery.trim().length === 0
    ) {
        return <Screens.Loading />;
    }
    if (customersOverviewStore.customersError) {
        return <Screens.Error message={customersOverviewStore.customersError.message} />;
    }
    const addCustomerButton = (
        <div className={styles.customerAddBtn}>
            <UI.Button
                to={Route.customersNew}
                label={t("screens.dashboard.customers.add_customer")}
                className="customer-button float-right w-100"
            />
        </div>
    );

    return (
        <React.Fragment>
            <UI.Container className="pt-4">
                <UI.Row>
                    <UI.Col md={9}>
                        <UI.Card>
                            <div className={styles.customerList}>
                                <CustomerList
                                    umsCustomers={customersOverviewStore.customers}
                                    totalEntries={customersOverviewStore.customersTotalEntries}
                                    currentPage={customersOverviewStore.currentPage}
                                    itemsPerPage={CustomersOverviewStore.ITEMS_PER_PAGE}
                                    onPageChanged={customersOverviewStore.onPageChanged}
                                    onSearch={customersOverviewStore.handleSearchInput}
                                    searchAppendElement={
                                        userRole !== CustomerRole.CUSTOMERSUPPORT ? addCustomerButton : undefined
                                    }
                                />
                            </div>
                        </UI.Card>
                    </UI.Col>
                    <UI.Col md={3}>
                        {customersOverviewStore.statisticsError ? (
                            <UI.Alert variant="danger">{customersOverviewStore.statisticsError.message}</UI.Alert>
                        ) : customersOverviewStore.statisticsLoading ||
                          customersOverviewStore.statistics === undefined ? (
                            <UI.LoadingCard />
                        ) : (
                            <CustomerOverviewStatistics statistics={customersOverviewStore.statistics} />
                        )}

                        {userRole ? (
                            userRole === CustomerRole.CUSTOMERDEV || authStore.isSupportUserInRole2ndLevel ? (
                                <UI.Card
                                    title={"Zusätzliche Aktionen"}
                                    className={`CardInner ${styles.additionalActions}`}
                                >
                                    {userRole === CustomerRole.CUSTOMERDEV && (
                                        <CustomerOverviewDialog
                                            buttonLabel="Repx aktualisieren"
                                            onToggleModal={toggleAcualizeRepxModal}
                                            modalTitle="Repx-Dateien aktualisieren"
                                            show={
                                                customersOverviewStore.activeModal ===
                                                CustomerOverviewModal.ACTUALIZE_REPX
                                            }
                                        >
                                            {customersOverviewStore.allCustomersForActionsLoading ? (
                                                <UI.LoadingCard />
                                            ) : customersOverviewStore.allCustomersForActionsError ? (
                                                <UI.Alert variant="danger">
                                                    {customersOverviewStore.allCustomersForActionsError.message}
                                                </UI.Alert>
                                            ) : (
                                                <CallServiceForAllCustomers
                                                    onCloseModal={toggleAcualizeRepxModal}
                                                    customers={customersOverviewStore.allCustomersForActions}
                                                    serviceEndpoint={NetworkConfig.actualizeCustomerRepxUrl}
                                                    serviceMethod="POST"
                                                    modalTitle="Willst du die Repx-Datei aller Kunden aktualisieren?"
                                                    finishedMessage="Die Aktualisierung ist abgeschlossen"
                                                />
                                            )}
                                        </CustomerOverviewDialog>
                                    )}
                                    {userRole === CustomerRole.CUSTOMERDEV && (
                                        <CustomerOverviewDialog
                                            buttonLabel="Dateibereinigung"
                                            onToggleModal={toggleCleanupFilesModal}
                                            modalTitle="Dateien bereinigen"
                                            show={
                                                customersOverviewStore.activeModal ===
                                                CustomerOverviewModal.CLEANUP_FILES
                                            }
                                        >
                                            {customersOverviewStore.allCustomersForActionsLoading ? (
                                                <UI.LoadingCard />
                                            ) : customersOverviewStore.allCustomersForActionsError ? (
                                                <UI.Alert variant="danger">
                                                    {customersOverviewStore.allCustomersForActionsError.message}
                                                </UI.Alert>
                                            ) : (
                                                <CallServiceForAllCustomers
                                                    onCloseModal={toggleCleanupFilesModal}
                                                    customers={customersOverviewStore.allCustomersForActions}
                                                    serviceEndpoint={NetworkConfig.cleanupFilesUrl}
                                                    serviceMethod="POST"
                                                    modalTitle="Willst du die Dateien aller Kunden bereinigen?"
                                                    finishedMessage="Die Dateibereinigung ist abgeschlossen"
                                                />
                                            )}
                                        </CustomerOverviewDialog>
                                    )}
                                    {userRole === CustomerRole.CUSTOMERDEV && (
                                        <CustomerOverviewDialog
                                            buttonLabel="App Minversion erhöhen"
                                            onToggleModal={toggleUpdateApiMinversionModal}
                                            modalTitle="Neue Inspection Minversion"
                                            show={
                                                customersOverviewStore.activeModal ===
                                                CustomerOverviewModal.UPDATE_API_MINVERSION
                                            }
                                        >
                                            <UpdateGeneralSettingDialog
                                                settingsKey="INSP.MINVERSION"
                                                emptyInputErrorMessage="Bitte eine neue Inspection Minversion eingeben"
                                                inputLabel="Format: xxx.yyy.zzzz (Beispiel: 3.1.162)"
                                                operationSuccessMessage="Die Inspection Minversion wurde erfolgreich aktualisiert"
                                                closeModal={toggleUpdateApiMinversionModal}
                                            />
                                        </CustomerOverviewDialog>
                                    )}
                                    {userRole === CustomerRole.CUSTOMERDEV && (
                                        <CustomerOverviewDialog
                                            buttonLabel="App CurrentVersion erhöhen"
                                            onToggleModal={toggleUpdateApiCurrentVersionModal}
                                            modalTitle="Neue Inspection CurrentVersion"
                                            show={
                                                customersOverviewStore.activeModal ===
                                                CustomerOverviewModal.UPDATE_API_CURRENTVERSION
                                            }
                                        >
                                            <UpdateGeneralSettingDialog
                                                settingsKey="INSP.CURRENTVERSION"
                                                emptyInputErrorMessage="Bitte eine neue Inspection Current Version eingeben"
                                                inputLabel="Format: xxx.yyy.zzzz (Beispiel: 3.1.162 )"
                                                operationSuccessMessage="Die Inspection CurrentVersion wurde erfolgreich aktualisiert"
                                                closeModal={toggleUpdateApiCurrentVersionModal}
                                            />
                                        </CustomerOverviewDialog>
                                    )}
                                    {userRole === CustomerRole.CUSTOMERDEV && (
                                        <UI.ConfirmationDialog
                                            buttonText="Postleitzahlen aktualisieren"
                                            modalTitle="Aktualisieren der Postleitzahlen"
                                            confirmationQuestion="Willst du die Postleitzahlen wirklich aktualisieren?"
                                            inProgressText="Postleitzahlen werden aktualisiert..."
                                            onConfirmation={onActualizeZipcodes}
                                            buttonClasses={"mr-2 d-inline mb-2 mb-sm-0 customer-button w-100"}
                                            isDangerousOperation
                                        />
                                    )}
                                    {getEnvironment() !== "prod" && (
                                        <CopyCustomerDialog label={t("screens.dashboard.copy_customer.title")} />
                                    )}
                                </UI.Card>
                            ) : null
                        ) : null}
                    </UI.Col>
                </UI.Row>
            </UI.Container>
        </React.Fragment>
    );
};

export const CustomersOverview = withRouter(Sentry.withProfiler(observer(CustomersOverviewBase)));
